import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ListChecks, Layers, Lock, Star, PanelLeftClose, PanelLeft, GripVertical, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import {
  DndContext, closestCenter, useSensor, useSensors, PointerSensor, KeyboardSensor,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { nexaApi, getKey, clearKey } from './api';
import { PasscodeScreen } from './PasscodeScreen';
import { ActiveSigns } from './ActiveSigns';
import { TodayView } from './TodayView';
import { LifeView } from './LifeView';
import { ChatView } from './ChatView';
import { SKILL_META, DEFAULT_ORDER, ORDER_KEY, orderSkillIds } from './skills';
import './nexa.css';

const MOBILE_TABS = [
  { id: 'today', label: 'Today', icon: ListChecks },
  { id: 'chat', label: 'NEXA', center: true },
  { id: 'life', label: 'Life', icon: Layers },
];

/* ------------------------- desktop left sidebar ------------------------- */
const SidebarSkill = ({ skill, active, fav, collapsed, onClick, onToggleFav }) => {
  const Icon = skill.icon;
  const locked = !skill.unlocked;
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: skill.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center rounded-xl transition-colors ${collapsed ? 'justify-center pr-0' : 'pr-1'} ${
        active ? 'bg-sky-400/10' : 'hover:bg-white/[0.04]'
      } ${isDragging ? 'bg-white/[0.06] shadow-[0_8px_24px_rgba(0,0,0,0.5)]' : ''}`}
    >
      {!collapsed && (
        <button
          {...attributes}
          {...listeners}
          data-testid={`desk-drag-${skill.id}`}
          aria-label="Drag to reorder"
          className="pl-1.5 py-2.5 text-white/20 hover:text-white/50 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity touch-none"
        >
          <GripVertical className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      )}
      <button
        onClick={onClick}
        title={collapsed ? skill.name : undefined}
        data-testid={`desk-nav-${skill.id}`}
        className={`flex-1 flex items-center gap-3 py-2.5 text-left min-w-0 ${collapsed ? 'justify-center px-0' : 'px-2'}`}
      >
        <Icon
          className={`w-[18px] h-[18px] shrink-0 transition-colors ${active ? 'text-sky-400' : 'text-white/45 group-hover:text-white/70'}`}
          strokeWidth={1.8}
        />
        {!collapsed && (
          <span
            className={`text-[13px] truncate transition-colors ${
              active ? 'text-sky-300' : 'text-white/70 group-hover:text-white/90'
            } ${locked ? 'opacity-40' : ''}`}
          >
            {skill.name}
          </span>
        )}
      </button>
      {!collapsed && (
        <button
          onClick={onToggleFav}
          data-testid={`desk-fav-${skill.id}`}
          aria-label={fav ? 'Unpin' : 'Pin to top'}
          aria-pressed={fav}
          className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 transition-opacity ${
            fav ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
        >
          <Star className={`w-3.5 h-3.5 ${fav ? 'fill-amber-400 text-amber-400' : 'text-white/45 hover:text-white/80'}`} strokeWidth={2} />
        </button>
      )}
    </div>
  );
};

const DesktopSidebar = ({
  activeSkillId, isToday, orderedIds, favorites, collapsed,
  onToday, onSkill, onToggleFav, onReorder, onToggleCollapse, onLock, width,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = orderedIds.indexOf(active.id);
    const newIndex = orderedIds.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(orderedIds, oldIndex, newIndex));
  };
  return (
    <aside
      className="fixed inset-y-0 left-0 z-40 flex flex-col border-r border-white/10 bg-black transition-[width] duration-200"
      style={{ width }}
      data-testid="nexa-sidebar-rail"
    >
      <div className={`flex items-center h-16 shrink-0 border-b border-white/10 ${collapsed ? 'justify-center px-0' : 'justify-between px-4'}`}>
        {!collapsed && (
          <div className="flex items-center gap-2.5 min-w-0">
            <img src="/nexa-nav-icon.png" alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
            <span className="font-mono tracking-[0.3em] text-xs text-white/80 uppercase">NEXA</span>
          </div>
        )}
        <button
          onClick={onToggleCollapse}
          data-testid="nexa-sidebar-toggle"
          aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-white/40 hover:text-white/80 hover:bg-white/[0.05] transition-colors"
        >
          {collapsed ? <PanelLeft className="w-4 h-4" strokeWidth={1.8} /> : <PanelLeftClose className="w-4 h-4" strokeWidth={1.8} />}
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-3 flex flex-col gap-0.5">
        <button
          onClick={onToday}
          title={collapsed ? 'Today' : undefined}
          data-testid="desk-nav-today"
          className={`flex items-center gap-3 py-2.5 rounded-xl transition-colors ${collapsed ? 'justify-center px-0' : 'px-3'} ${
            isToday ? 'bg-sky-400/10 text-sky-300' : 'text-white/70 hover:bg-white/[0.04] hover:text-white/90'
          }`}
        >
          <ListChecks className={`w-[18px] h-[18px] shrink-0 ${isToday ? 'text-sky-400' : 'text-white/45'}`} strokeWidth={1.8} />
          {!collapsed && <span className="text-[13px] font-medium">Today</span>}
        </button>
        <div className="h-px bg-white/[0.06] my-2.5 mx-2" />
        {!collapsed && (
          <span className="px-3 pb-1.5 text-[10px] font-mono tracking-[0.22em] uppercase text-white/30">Pages</span>
        )}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
            {orderedIds.map((id) => {
              const s = SKILL_META.find((x) => x.id === id);
              if (!s) return null;
              return (
                <SidebarSkill
                  key={id}
                  skill={s}
                  active={activeSkillId === id}
                  fav={favorites.includes(id)}
                  collapsed={collapsed}
                  onClick={() => onSkill(s)}
                  onToggleFav={() => onToggleFav(id)}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      </nav>
      <button
        onClick={onLock}
        data-testid="nexa-rail-lock"
        aria-label="Lock NEXA"
        className={`m-3 h-10 rounded-xl flex items-center justify-center gap-2 text-white/30 hover:text-white/70 hover:bg-white/[0.04] transition-colors`}
      >
        <Lock className="w-4 h-4" strokeWidth={1.8} />
        {!collapsed && <span className="text-[11px] font-mono tracking-[0.2em] uppercase">Lock</span>}
      </button>
    </aside>
  );
};

export default function NexaApp() {
  const [unlocked, setUnlocked] = useState(null);
  const [tab, setTab] = useState('chat'); // mobile only
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );
  const [skillId, setSkillId] = useState(() => {
    const m = /^#life\/([\w-]+)/.exec(window.location.hash);
    return m ? m[1] : null;
  });
  const [favorites, setFavorites] = useState([]);
  const [order, setOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(ORDER_KEY) || 'null');
      if (Array.isArray(saved) && saved.length) return saved;
    } catch {
      /* ignore */
    }
    return DEFAULT_ORDER;
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('nexa_sidebar_collapsed') === '1',
  );
  const [chatWidth, setChatWidth] = useState(() => {
    const saved = parseInt(localStorage.getItem('nexa_chat_width') || '', 10);
    if (saved && !Number.isNaN(saved)) return saved;
    return Math.round(Math.min(560, Math.max(360, window.innerWidth * 0.25)));
  });
  const [chatCollapsed, setChatCollapsed] = useState(
    () => localStorage.getItem('nexa_chat_collapsed') === '1',
  );

  // Track the desktop/mobile breakpoint so only one layout tree mounts.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const on = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  // Keep the selected page in sync with the #life/<id> hash.
  useEffect(() => {
    const on = () => {
      const m = /^#life\/([\w-]+)/.exec(window.location.hash);
      setSkillId(m ? m[1] : null);
    };
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);

  useEffect(() => {
    if (unlocked) {
      nexaApi.get('/life/favorites').then(({ data }) => setFavorites(data.ids || [])).catch(() => {});
    }
  }, [unlocked]);

  const toggleFav = (id) =>
    setFavorites((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      nexaApi.put('/life/favorites', { ids: next }).catch(() => {});
      return next;
    });

  const reorderPages = (nextIds) => {
    setOrder(nextIds);
    localStorage.setItem(ORDER_KEY, JSON.stringify(nextIds));
  };

  const toggleCollapse = () =>
    setSidebarCollapsed((v) => {
      const n = !v;
      localStorage.setItem('nexa_sidebar_collapsed', n ? '1' : '0');
      return n;
    });

  const toggleChatCollapse = () =>
    setChatCollapsed((v) => {
      const n = !v;
      localStorage.setItem('nexa_chat_collapsed', n ? '1' : '0');
      return n;
    });

  useEffect(() => {
    localStorage.setItem('nexa_chat_width', String(chatWidth));
  }, [chatWidth]);

  const startResize = (e) => {
    e.preventDefault();
    const onMove = (ev) => {
      const w = window.innerWidth - ev.clientX;
      setChatWidth(Math.max(320, Math.min(Math.round(window.innerWidth * 0.55), w)));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const orderedIds = orderSkillIds(order, favorites);
  const sidebarWidth = sidebarCollapsed ? 68 : 240;

  useEffect(() => {
    // Hard rule: no page-embedded media may ever play inside NEXA.
    const guard = (e) => {
      const el = e.target;
      if (el && (el.tagName === 'AUDIO' || el.tagName === 'VIDEO')) {
        try { el.pause(); el.muted = true; } catch { /* noop */ }
      }
    };
    document.addEventListener('play', guard, true);
    return () => document.removeEventListener('play', guard, true);
  }, []);

  useEffect(() => {
    document.title = 'NEXA';
    const manifest = document.querySelector("link[rel='manifest']");
    if (manifest) manifest.setAttribute('href', '/nexa-manifest.json');
    let theme = document.querySelector("meta[name='theme-color']");
    if (!theme) {
      theme = document.createElement('meta');
      theme.name = 'theme-color';
      document.head.appendChild(theme);
    }
    theme.setAttribute('content', '#000000');
    const metas = [
      ['apple-mobile-web-app-capable', 'yes'],
      ['apple-mobile-web-app-status-bar-style', 'black-translucent'],
      ['apple-mobile-web-app-title', 'NEXA'],
    ];
    metas.forEach(([name, content]) => {
      let el = document.querySelector(`meta[name='${name}']`);
      if (!el) {
        el = document.createElement('meta');
        el.name = name;
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    });
    let touchIcon = document.querySelector("link[rel='apple-touch-icon']");
    if (!touchIcon) {
      touchIcon = document.createElement('link');
      touchIcon.rel = 'apple-touch-icon';
      document.head.appendChild(touchIcon);
    }
    touchIcon.setAttribute('href', '/nexa-icon-192.png');
    const NEXA_URL = 'https://levanta.life/nexa';
    const setLink = (rel, href) => {
      let el = document.querySelector(`link[rel='${rel}']`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };
    const setMetaProp = (property, content) => {
      let el = document.querySelector(`meta[property='${property}']`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setLink('canonical', NEXA_URL);
    setMetaProp('og:url', NEXA_URL);
    setMetaProp('twitter:url', NEXA_URL);
    document.body.style.background = '#000';
  }, []);

  useEffect(() => {
    if (!getKey()) {
      setUnlocked(false);
      return;
    }
    nexaApi
      .post('/auth', { passcode: getKey() })
      .then(() => setUnlocked(true))
      .catch(() => {
        clearKey();
        setUnlocked(false);
      });
  }, []);

  const selectSkill = (s) => {
    if (!s.unlocked) {
      toast(`${s.name} isn't tracked yet.`, {
        description: `Tell NEXA in chat to start tracking ${s.name.toLowerCase()} — she'll unlock this page.`,
      });
      return;
    }
    window.location.hash = `#life/${s.id}`;
    setSkillId(s.id);
  };

  const selectToday = () => {
    if (window.location.hash.startsWith('#life/')) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    setSkillId(null);
  };

  const lock = () => { clearKey(); setUnlocked(false); };

  if (unlocked === null) {
    return (
      <div className="nexa-root flex items-center justify-center min-h-[100dvh]">
        <div className="w-6 h-6 border-2 border-sky-400/30 border-t-sky-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div className="nexa-root nexa-noise">
        <PasscodeScreen onUnlock={() => setUnlocked(true)} />
      </div>
    );
  }

  /* ------------------------------ DESKTOP ------------------------------ */
  if (isDesktop) {
    return (
      <div className="nexa-root nexa-noise" data-testid="nexa-app">
        <Toaster theme="dark" position="top-center" />
        <DesktopSidebar
          activeSkillId={skillId}
          isToday={!skillId}
          orderedIds={orderedIds}
          favorites={favorites}
          collapsed={sidebarCollapsed}
          width={sidebarWidth}
          onToday={selectToday}
          onSkill={selectSkill}
          onToggleFav={toggleFav}
          onReorder={reorderPages}
          onToggleCollapse={toggleCollapse}
          onLock={lock}
        />
        <main
          className="fixed top-0 bottom-0 overflow-y-auto transition-[left,right] duration-200"
          style={{ left: sidebarWidth, right: chatCollapsed ? 48 : chatWidth }}
          data-testid="nexa-desktop-main"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={skillId ? 'skill' : 'today'}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
            >
              {skillId ? <LifeView /> : <TodayView />}
            </motion.div>
          </AnimatePresence>
        </main>
        {chatCollapsed ? (
          <aside
            className="fixed top-0 bottom-0 right-0 z-30 border-l border-white/10 bg-black flex flex-col items-center pt-4 gap-4"
            style={{ width: 48 }}
            data-testid="nexa-desktop-chat-collapsed"
          >
            <button
              onClick={toggleChatCollapse}
              data-testid="nexa-chat-expand"
              title="Open chat"
              className="w-9 h-9 rounded-xl border border-white/10 bg-white/[0.04] flex items-center justify-center text-white/70 hover:text-sky-300 hover:border-sky-400/40 transition-colors"
            >
              <PanelRightOpen className="w-4 h-4" />
            </button>
            <span className="mt-1 font-mono tracking-[0.3em] text-[10px] text-white/40 uppercase [writing-mode:vertical-rl] rotate-180">
              Chat
            </span>
          </aside>
        ) : (
          <aside
            className="fixed top-0 bottom-0 right-0 z-30 border-l border-white/10 bg-black flex flex-col"
            style={{ width: chatWidth }}
            data-testid="nexa-desktop-chat"
          >
            <div
              onPointerDown={startResize}
              onDoubleClick={() => setChatWidth(Math.round(Math.min(560, Math.max(360, window.innerWidth * 0.25))))}
              data-testid="nexa-chat-resize"
              role="separator"
              aria-orientation="vertical"
              title="Drag to resize · double-click to reset"
              className="group absolute left-0 inset-y-0 w-2 -translate-x-1/2 cursor-col-resize z-40 flex items-center justify-center"
            >
              <span className="w-[3px] h-16 rounded-full bg-white/10 group-hover:bg-sky-400/60 transition-colors" />
            </div>
            <div className="px-5 h-16 flex items-center gap-2.5 border-b border-white/10 shrink-0">
              <img src="/nexa-nav-icon.png" alt="" className="w-8 h-8 rounded-full object-cover border border-sky-400/40" />
              <span className="font-mono tracking-[0.25em] text-xs text-white/70 uppercase">NEXA</span>
              <button
                onClick={toggleChatCollapse}
                data-testid="nexa-chat-collapse"
                title="Fold chat to the right"
                className="ml-auto w-8 h-8 rounded-lg border border-white/10 flex items-center justify-center text-white/55 hover:text-sky-300 hover:border-sky-400/40 transition-colors"
              >
                <PanelRightClose className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 min-h-0 flex flex-col px-4 pt-3">
              <ChatView />
            </div>
          </aside>
        )}
      </div>
    );
  }

  /* ------------------------------ MOBILE ------------------------------ */
  return (
    <div className="nexa-root nexa-noise" data-testid="nexa-app">
      <Toaster theme="dark" position="top-center" />
      <div className={`max-w-md mx-auto relative flex flex-col ${tab === 'chat' ? 'h-[100dvh] overflow-hidden' : 'min-h-[100dvh]'}`}>
        <main
          className={`flex-1 px-6 pt-14 pb-28 flex flex-col ${tab === 'chat' ? 'min-h-0' : ''}`}
          style={{ paddingTop: 'max(3.5rem, env(safe-area-inset-top))' }}
        >
          <ActiveSigns />
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className={tab === 'chat' ? 'flex flex-col flex-1 min-h-0' : ''}
            >
              {tab === 'chat' && <ChatView />}
              {tab === 'today' && <TodayView />}
              {tab === 'life' && <LifeView />}
            </motion.div>
          </AnimatePresence>
        </main>

        <nav
          className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md backdrop-blur-2xl bg-black/60 border-t border-white/10"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          data-testid="nexa-bottom-nav"
        >
          <div className="flex items-end">
            {MOBILE_TABS.map(({ id, label, icon: Icon, center }) =>
              center ? (
                <div key={id} className="flex-1 relative h-16">
                  <div className="absolute left-1/2 -top-7 -translate-x-1/2">
                    <motion.button
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setTab(id)}
                      data-testid={`nexa-nav-${id}`}
                      aria-label="NEXA"
                      className={`w-[68px] h-[68px] rounded-full bg-black border overflow-hidden transition-shadow ${
                        tab === id
                          ? 'border-sky-400/60 shadow-[0_0_28px_rgba(56,189,248,0.5)]'
                          : 'border-white/15 shadow-[0_0_14px_rgba(56,189,248,0.18)]'
                      }`}
                    >
                      <img src="/nexa-nav-icon.png" alt="NEXA" className="w-full h-full object-cover" />
                    </motion.button>
                  </div>
                </div>
              ) : (
                <motion.button
                  key={id}
                  whileTap={{ scale: 0.92 }}
                  onClick={() => {
                    if (id === 'life' && tab === 'life' && window.location.hash.startsWith('#life/')) {
                      window.location.hash = '#life';
                    }
                    setTab(id);
                  }}
                  data-testid={`nexa-nav-${id}`}
                  className="flex-1 h-16 flex flex-col items-center justify-center gap-1"
                >
                  <Icon
                    className={`w-5 h-5 transition-colors ${tab === id ? 'text-sky-400' : 'text-white/40'}`}
                    strokeWidth={1.7}
                  />
                  <span
                    className={`text-[10px] tracking-[0.15em] uppercase transition-colors ${
                      tab === id ? 'text-sky-400' : 'text-white/40'
                    }`}
                  >
                    {label}
                  </span>
                </motion.button>
              ),
            )}
          </div>
        </nav>
      </div>
    </div>
  );
}
